import apiClient from '../api/client';

export type SundayKey = 's1' | 's2' | 's3' | 's4' | 's5';

export type WizardStep = 'chat' | 'people' | 'generosity' | 'thanks' | null;

export type ChatMessage = { role: 'user' | 'assistant'; content: string };

export type GuidedSection = 'people' | 'generosity' | 'review';

/** In-app we only edit worship attendance; API may return legacy weekly_metrics keys. */
export interface WorshipWeeklyState {
  worshipAttendance: Record<SundayKey, number>;
}

export interface GivingBreakdown {
  income: {
    congregationalOffering: number | null;
    sponsoringChurches: number | null;
    associations: number | null;
    mbcb: number | null;
    otherSupport: number | null;
    total: number | null;
  };
  missionGiving: {
    cooperativeProgram: number | null;
    associations: number | null;
    margaretLackey: number | null;
    otherMissionCauses: number | null;
    total: number | null;
  };
}

export interface MBCBReport {
  id: number;
  planterId: number;
  reportMonth: number;
  reportYear: number;
  status: string;
  sundayAttendance?: number;
  smallGroupAttendance?: number;
  newMembers?: number;
  baptisms?: number;
  salvations?: number;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  buildingFund?: number;
  missionsGiving?: number;
  ministryHighlights?: string;
  prayerRequests?: string;
  supportNeeded?: string;
  additionalNotes?: string;
  nextMonthFocus?: string;
  attendanceNotes?: string | null;
  givingNotes?: string | null;
  weeklyMetrics?: Record<string, unknown>;
  givingBreakdown?: Partial<GivingBreakdown>;
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string | null;
}

export interface PlanterProfile {
  id: number;
  churchName?: string | null;
  mbcbReportingEnabled?: boolean;
}

export const SUNDAYS: SundayKey[] = ['s1', 's2', 's3', 's4', 's5'];

export const emptySundayRow = (): Record<SundayKey, number> => ({
  s1: 0,
  s2: 0,
  s3: 0,
  s4: 0,
  s5: 0,
});

export const defaultWeekly = (): WorshipWeeklyState => ({
  worshipAttendance: emptySundayRow(),
});

export const defaultGiving = (): GivingBreakdown => ({
  income: {
    congregationalOffering: null,
    sponsoringChurches: null,
    associations: null,
    mbcb: null,
    otherSupport: null,
    total: null,
  },
  missionGiving: {
    cooperativeProgram: null,
    associations: null,
    margaretLackey: null,
    otherMissionCauses: null,
    total: null,
  },
});

export const reportActionLabel = (status?: string) =>
  status === 'needs_revision'
    ? 'Continue report'
    : status === 'draft'
      ? 'Open report'
      : 'View report';

export const statusBadgeClasses = (status?: string) => {
  switch (status) {
    case 'draft':
      return 'bg-primary-100 text-primary-900';
    case 'needs_revision':
      return 'bg-amber-100 text-amber-900';
    case 'submitted':
      return 'bg-blue-100 text-blue-900';
    case 'under_review':
      return 'bg-indigo-100 text-indigo-900';
    case 'approved':
      return 'bg-emerald-100 text-emerald-900';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const statusDisplayLabel = (status?: string) =>
  (status || 'unknown').replace(/_/g, ' ');

export const mergeWeekly = (raw?: Record<string, unknown> | null): WorshipWeeklyState => {
  const base = defaultWeekly();
  if (!raw || typeof raw !== 'object') return base;
  const w = raw.worshipAttendance;
  if (!w || typeof w !== 'object') return base;
  const row = w as Record<string, unknown>;
  SUNDAYS.forEach((sk) => {
    const n = row[sk];
    base.worshipAttendance[sk] = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  });
  return base;
};

export const mergeGiving = (raw?: Partial<GivingBreakdown>): GivingBreakdown => {
  const base = defaultGiving();
  if (!raw) return base;
  if (raw.income) {
    Object.assign(base.income, raw.income);
  }
  if (raw.missionGiving) {
    Object.assign(base.missionGiving, raw.missionGiving);
  }
  return base;
};

export const sumWorship = (w: WorshipWeeklyState) =>
  SUNDAYS.reduce((acc, k) => acc + (w.worshipAttendance[k] || 0), 0);

/**
 * Average Sunday worship attendance across weeks actually reported (zero/blank weeks are
 * excluded rather than pulling the average down). Matches backend
 * `mbcbReportAvgSundayAttendance` in planterPipelineService.js and iOS
 * `PlanterDetailView.attendanceAverage`, which is what network-wide rollups use.
 */
export const avgWorship = (w: WorshipWeeklyState) => {
  const positives = SUNDAYS.map((k) => w.worshipAttendance[k] || 0).filter((n) => n > 0);
  if (positives.length === 0) return 0;
  return positives.reduce((a, b) => a + b, 0) / positives.length;
};

/** Uses /planters/me (not /planters/user/:id) so church_planter role is not blocked by forbidRoles. */
export const fetchMyPlanterProfile = async () => {
  const res = await apiClient.get('/planters/me');
  return res.data.data as PlanterProfile;
};

/** Uses /mbcb/reports/me so planters are not blocked from /planters/:id/reports. */
export const fetchReports = async () => {
  const res = await apiClient.get('/mbcb/reports/me');
  const { reports } = res.data.data as { reports: MBCBReport[] };
  return reports;
};

export const fetchReport = async (reportId: number) => {
  const res = await apiClient.get(`/mbcb/reports/${reportId}`);
  return res.data.data as MBCBReport;
};

export const loadPersistedChat = (reportId: number): ChatMessage[] => {
  try {
    const raw = localStorage.getItem(`mbcb-ai-chat-v1-${reportId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m != null &&
        typeof m === 'object' &&
        (m.role === 'user' || m.role === 'assistant') &&
        typeof m.content === 'string',
    );
  } catch {
    return [];
  }
};

export const persistChat = (reportId: number, messages: ChatMessage[]) => {
  try {
    if (messages.length === 0) {
      localStorage.removeItem(`mbcb-ai-chat-v1-${reportId}`);
      return;
    }
    localStorage.setItem(`mbcb-ai-chat-v1-${reportId}`, JSON.stringify(messages));
  } catch {
    // localStorage may be unavailable; chat remains ephemeral for this session
  }
};

export const clearPersistedChat = (reportId: number) => {
  try {
    localStorage.removeItem(`mbcb-ai-chat-v1-${reportId}`);
  } catch {
    // ignore
  }
};
